import { AtButtonStyles, AtButtonSizeWithText, AtButtonSizeWithoutText } from './at-button-styles'
import { AtButtonTypes, AtButtonDimensions, AtButtonBehaviors } from '../../types'
import { AtIcon, IconRef } from '../at-icon'
import dynamic from 'next/dynamic'
import spinnerData from './assets/spinner.json'
import { forwardRef, MouseEvent, MouseEventHandler, useState, useEffect } from 'react'
import { makeCtaAnalyticsClickHandler, useCtaAnalytics } from '../../utils/cta-analytics'

const Lottie = dynamic(() => import('react-lottie'), {
  ssr: false,
  loading: () => (
    <div className="w-[50px] h-[50px] animate-spin rounded-full border-2 border-current border-t-transparent" />
  ),
})

export interface AtButtonProps {
  id?: string
  title?: string
  /** **Use only sparingly**, for example, when the button is just an icon with no text, because **this overrides completely the text content of the button** for a11y users (meaning the visible text of the button is not what's read to them) */
  ariaLabel?: string
  type: AtButtonTypes
  size: AtButtonDimensions
  isDisabled?: boolean
  iconLeft?: string
  iconRight?: string
  align?: string
  IconLeftRef?: IconRef
  IconRightRef?: IconRef
  iconLeftClasses?: string
  iconRightClasses?: string
  className?: string
  typeButton?: AtButtonBehaviors
  openUrl?: string
  target?: '_blank' | '_self'
  form?: string
  onClick?: MouseEventHandler<HTMLButtonElement>
  parentName?: string
  onTrackClick?: (event?: MouseEvent<HTMLButtonElement>) => void
  isLink?: boolean
}

export const AtButton = forwardRef<HTMLButtonElement, AtButtonProps>(
  (
    {
      id,
      title,
      ariaLabel,
      type,
      size,
      isDisabled,
      iconLeft,
      iconLeftClasses = '',
      iconRight,
      iconRightClasses = '',
      IconLeftRef,
      IconRightRef,
      className,
      align,
      typeButton = AtButtonBehaviors.BUTTON,
      onClick,
      openUrl,
      target = '_self',
      form,
      parentName = 'not available',
      onTrackClick,
      isLink = false,
    }: AtButtonProps,
    ref
  ) => {
    const [mounted, setMounted] = useState(false)

    useEffect(() => {
      setMounted(true)
    }, [])

    const baseClass = `flex items-center ${align && `justify-${align}`}`
    const baseFocusClass =
      'focus:outline-none focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline focus-visible:outline-neutrals-900'

    const notGrayscale = type !== 'grayscale'

    const defaultOptionsSpinner = {
      loop: true,
      autoplay: true,
      animationData: spinnerData,
      rendererSettings: {
        preserveAspectRatio: 'xMidYMid slice',
      },
    }

    const { logClick } = useCtaAnalytics()

    if (isLink) {
      return (
        <a
          href={openUrl}
          target={target}
          id={id}
          aria-label={ariaLabel}
          className={`justify-center ${baseClass} ${baseFocusClass} ${
            notGrayscale ? 'uppercase leading-1.4 tracking-[0.16em]' : 'px-0'
          } ${title ? AtButtonSizeWithText[size] : AtButtonSizeWithoutText[size]} ${
            isDisabled
              ? `${AtButtonStyles[type].disabled}`
              : `${AtButtonStyles[type].hover} ${AtButtonStyles[type].active} ${AtButtonStyles[type].focus} ${AtButtonStyles[type].base}`
          } ${className}`}
        >
          {IconLeftRef ? (
            <IconLeftRef
              className={`w-10 px-2 inline ${(IconRightRef || title) && notGrayscale ? 'mr-2' : ''} ${iconLeftClasses}`}
            />
          ) : (
            iconLeft && (
              <AtIcon
                src={iconLeft}
                className={`w-10 px-2 inline ${(iconRight || title) && notGrayscale ? 'mr-2' : ''} ${iconLeftClasses}`}
              />
            )
          )}
          {title}
          {isDisabled && !title && !IconRightRef && !iconRight && !IconLeftRef && !iconLeft && mounted && (
            <Lottie options={defaultOptionsSpinner} height={50} width={50} />
          )}
          {IconRightRef ? (
            <IconRightRef className={`w-10 inline px-2 ${title && notGrayscale ? 'ml-2' : ''} ${iconRightClasses}`} />
          ) : (
            iconRight && (
              <AtIcon
                src={iconRight}
                className={`w-10 px-2 inline ${title && notGrayscale ? 'ml-2' : ''} ${iconRightClasses}`}
              />
            )
          )}
        </a>
      )
    }

    return (
      <button
        id={id}
        form={form}
        ref={ref}
        aria-label={ariaLabel}
        className={`justify-center ${baseClass} ${baseFocusClass} ${
          notGrayscale ? 'uppercase leading-1.4 tracking-[0.16em]' : 'px-0'
        } ${title ? AtButtonSizeWithText[size] : AtButtonSizeWithoutText[size]} ${
          isDisabled
            ? `${AtButtonStyles[type].disabled}`
            : `${AtButtonStyles[type].hover} ${AtButtonStyles[type].active} ${AtButtonStyles[type].focus} ${AtButtonStyles[type].base}`
        } ${className}`}
        disabled={isDisabled}
        onClick={makeCtaAnalyticsClickHandler(
          logClick,
          {
            text: title ?? '',
            href: openUrl ?? '',
            style: type === AtButtonTypes.TERTIARY || type === AtButtonTypes.GRAYSCALE ? 'text link' : 'button',
            component: parentName,
          },
          (event) => {
            if (onTrackClick) onTrackClick(event)
            if (openUrl) {
              window.open(openUrl, target)
            } else if (onClick) {
              onClick(event)
            }
          }
        )}
        type={typeButton}
      >
        {IconLeftRef ? (
          <IconLeftRef
            className={`w-10 px-2 inline ${(IconRightRef || title) && notGrayscale ? 'mr-2' : ''} ${iconLeftClasses}`}
          />
        ) : (
          iconLeft && (
            <AtIcon
              src={iconLeft}
              className={`w-10 px-2 inline ${(iconRight || title) && notGrayscale ? 'mr-2' : ''} ${iconLeftClasses}`}
            />
          )
        )}
        {title}
        {isDisabled && !title && !IconRightRef && !iconRight && !IconLeftRef && !iconLeft && mounted && (
          <Lottie options={defaultOptionsSpinner} height={50} width={50} />
        )}
        {IconRightRef ? (
          <IconRightRef className={`w-10 inline px-2 ${title && notGrayscale ? 'ml-2' : ''} ${iconRightClasses}`} />
        ) : (
          iconRight && (
            <AtIcon
              src={iconRight}
              className={`w-10 px-2 inline ${title && notGrayscale ? 'ml-2' : ''} ${iconRightClasses}`}
            />
          )
        )}
      </button>
    )
  }
)
